import { checkDeviceType, checkIsFromLiliApp } from '@Services/utils/utils.ts';
import { DEFAULT_PAGE_INDEX } from '@Router/router.consts.ts';
import {
  AbTestFlowIds,
  AppStateTypes,
  OnboardingFlowIdentifier,
  OnboardingFlowType,
} from '../../../types/onboarding.types.ts';
import type { StateCreator } from 'zustand';
import type { IOnboardingSlice, RouterData } from '../../../types/onboarding.types.ts';
import type { RoutesMap } from '@Router/router.types.ts';

export const createOnboardingSlice: StateCreator<IOnboardingSlice> = (set, get) => ({
  onboardingSlice: {
    emailParam: '',
    isAppSubmitted: false,
    isOnboardingLocked: false,
    navigationStack: [],
    pickupWhereLeftNavigationStack: [],
    appState: AppStateTypes.PRELOAD,
    editMode: false,
    currentPageIndex: DEFAULT_PAGE_INDEX,
    progressValue: 0,
    flowType: OnboardingFlowType.REGULAR,
    isFromLiliApp: checkIsFromLiliApp(),
    isPartnerFlow: false,
    partnerName: '',
    discountPartnerId: '',
    onboardingFlowIdentifier: OnboardingFlowIdentifier.DIRECT,
    abTestFlowId: AbTestFlowIds.CONTROL,
    onboardingStartTime: Date.now(),
    deviceType: checkDeviceType(),
    partnerToken: '',
    customerKey: '',
    customerId: '',
    authorizedUserId: '',
    multiBusinessId: '',
    uboId: '',
    referralCode: '',
    sourcePlatform: '',
    adSource: '',
    isLoginProcessFinished: false,
    isPlanActivelySelected: false,
    routerData: { prevRoute: '', currentRoute: '' },
    deviceId: '',
    countries: [],
    preSelectedAnnualPlan: false,
    isAnnualPlanSelected: false,
    bufferedPoa: {
      type: null,
      blob: null,
    },
    globalCardFirstName: '',
    globalCardLastName: '',
    activeUboFirstName: '',
    activeUboLastName: '',
    loginToken: '',
    isWhiteGloveFlow: false,
    continueMultiBusiness: false,
    variationId: '',
    setIsOnboardingLocked: (value: boolean) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          isOnboardingLocked: value,
        },
      }),
    setBufferedPoa: (value) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          bufferedPoa: value,
        },
      }),
    setRouterData: (value: RouterData) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          routerData: value,
        },
      }),
    setIsLoginProcessFinished: (value: boolean) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          isLoginProcessFinished: value,
        },
      }),
    setQueryParamValue: (key: string, value: string) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          [key]: value,
        },
      }),
    setAppState: (value: AppStateTypes) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          appState: value,
        },
      }),
    setNavigationStack: (value: RoutesMap[]) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          navigationStack: value,
        },
      }),
    setPickupWhereLeftNavigationStack: (value: RoutesMap[]) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          pickupWhereLeftNavigationStack: value,
        },
      }),
    setIsPartnerFlow: (value: boolean) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          isPartnerFlow: value,
        },
      }),
    setPartnerName: (value: string) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          partnerName: value,
        },
      }),
    setOnboardingIdentifier: (value: OnboardingFlowIdentifier) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          onboardingFlowIdentifier: value,
        },
      }),
    setEditMode: (value: boolean) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          editMode: value,
        },
      }),
    setCurrentPageIndex: (value: number) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          currentPageIndex: value,
        },
      }),
    setProgressValue: (value: number) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          progressValue: value,
        },
      }),
    setFlowType: (value: OnboardingFlowType) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          flowType: value,
        },
      }),
    setAbTestFlowId: (value: AbTestFlowIds) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          abTestFlowId: value,
        },
      }),
    getOnboardingData: () => {
      return { ...get().onboardingSlice };
    },
    getIsEmbedded: () => {
      return (
        get().onboardingSlice.onboardingFlowIdentifier === OnboardingFlowIdentifier.PARTNER_EMBEDDED
      );
    },
    setPlanActivelySelected: (value: boolean) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          isPlanActivelySelected: value,
        },
      }),
    setCountries(value) {
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          countries: value,
        },
      });
    },
    setDeviceId: (value: string) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          deviceId: value,
        },
      }),
    setIsAnnualPlanSelected: (value: boolean) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          isAnnualPlanSelected: value,
        },
      }),
    setSelectedPlan: (value) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          selectedPlan: value,
        },
      }),
    setPreSelectedAnnualPlan: (value: boolean) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          preSelectedAnnualPlan: value,
        },
      }),
    setIsAppSubmitted: (value: boolean) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          isAppSubmitted: value,
        },
      }),
    setPartnerLabels: (value: { disclaimer: string; message: string; title: string }[]) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          partnerLabels: value,
        },
      }),
    setGlobalCardName: (firstName: string, lastName: string) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          globalCardFirstName: firstName,
          globalCardLastName: lastName,
        },
      }),
    setActiveUboName: (firstName: string, lastName: string) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          activeUboFirstName: firstName,
          activeUboLastName: lastName,
        },
      }),
    setIsWhiteGloveFlow: (value: boolean) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          isWhiteGloveFlow: value,
        },
      }),
    setVariationId: (value: string) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          variationId: value,
        },
      }),
    getIsWhiteGloveFlow: () => {
      return get().onboardingSlice.isWhiteGloveFlow;
    },
  },
});
